import { useEffect, useState } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { X } from 'lucide-react'
import { getVaultFile, getVaultFileObjectUrl, type VaultFileResult } from '@/lib/api'
import { obsidianUrlToPath, prepMarkdownForView, stripTimestamp } from '../lib'

export interface ViewTarget {
  path: string
  /** Display label — falls back to the filename. */
  name?: string
  meta?: string // "domain / workstream"
}

// react-markdown strips non-http(s)/mailto schemes by default — pass
// obsidian:// (intercepted below, opens in-viewer) and message:// (Mail.app
// on the Mac; iOS has no handler — desktop affordance) through.
function urlTransform(url: string): string {
  if (url.startsWith('obsidian://') || url.startsWith('message://')) return url
  return defaultUrlTransform(url)
}

/** Read-only file viewer for the web Cockpit. Folders resolve server-side to
 * their folder note; markdown renders with the concierge prose styling;
 * PDFs/images stream as binary and render natively. obsidian:// links inside
 * notes navigate the viewer to the referenced block/card instead of trying
 * to launch Obsidian. View-only by design — editing happens through
 * Concierge chat or Obsidian. */
export function FileViewer({
  target,
  onClose,
  onNavigate,
}: {
  target: ViewTarget | null
  onClose: () => void
  /** Open another vault path in the viewer (obsidian:// link clicks). */
  onNavigate: (path: string) => void
}) {
  const [result, setResult] = useState<VaultFileResult | null>(null)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!target) return
    let cancelled = false
    let createdUrl: string | null = null
    setResult(null)
    setObjectUrl(null)
    setError(null)
    setLoading(true)
    getVaultFile(target.path)
      .then(async (r) => {
        if (cancelled) return
        setResult(r)
        if (r.kind === 'binary') {
          createdUrl = await getVaultFileObjectUrl(r.path)
          if (cancelled) {
            URL.revokeObjectURL(createdUrl)
            return
          }
          setObjectUrl(createdUrl)
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [target])

  useEffect(() => {
    if (!target) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [target, onClose])

  if (!target) return null

  const title = stripTimestamp(target.name || target.path.split('/').pop() || '')
  const isImage = result?.kind === 'binary' && result.media_type.startsWith('image/')
  const isPdf = result?.kind === 'binary' && result.media_type === 'application/pdf'

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="concierge-root bg-background text-foreground border border-border w-full md:w-[720px] md:max-w-[94vw] md:max-h-[88dvh] h-full md:h-auto flex flex-col">
        <div className="flex-shrink-0 flex items-center gap-3 px-4 h-12 border-b border-border min-w-0">
          <div className="flex-1 min-w-0">
            <div className="text-[14px] text-foreground truncate">{title}</div>
            {target.meta && <div className="text-[11px] text-muted-foreground truncate">{target.meta}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-5 pb-safe">
          {loading && <div className="text-[13px] text-muted-foreground">Loading…</div>}
          {error && <div className="text-[13px] text-red-400 break-words">{error}</div>}
          {result?.kind === 'text' && (
            <>
              <div className="concierge-prose text-[14px] leading-relaxed">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  urlTransform={urlTransform}
                  components={{
                    a: ({ href, children }) => {
                      const vaultPath = href ? obsidianUrlToPath(href) : null
                      if (vaultPath) {
                        return (
                          <a
                            href={href}
                            onClick={(e) => {
                              e.preventDefault()
                              onNavigate(vaultPath)
                            }}
                          >
                            {children}
                          </a>
                        )
                      }
                      const external = href?.startsWith('http')
                      return (
                        <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>
                          {children}
                        </a>
                      )
                    },
                  }}
                >
                  {prepMarkdownForView(result.content)}
                </ReactMarkdown>
              </div>
              {result.truncated && (
                <div className="text-[11px] text-muted-foreground mt-4">Truncated — open in Obsidian for the full file.</div>
              )}
            </>
          )}
          {isImage && objectUrl && <img src={objectUrl} alt={title} className="max-w-full h-auto" />}
          {isPdf && objectUrl && (
            <iframe src={objectUrl} title={title} className="w-full h-[75dvh] border border-border bg-white" />
          )}
        </div>
      </div>
    </div>
  )
}
